import { useState } from 'react';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { SETTINGS_PAGE } from './settings-shared';
import { useNotificationSettings, useUpdateNotificationSettings } from './use-notification-settings';

/**
 * "Learn about people from my connected inbox" (Recipient field 41, `learning_settings.connected_inbox`).
 * When ON, a nightly server pass reads the two-way threads in the mailboxes and DM inboxes the person
 * connected and learns who people are to them. It is silent — no chat message announces it — so this
 * switch is the only control. OFF stops the learning; what was already learned stays.
 *
 * The same contract as iOS `SettingsScreen.inboxLearningCard` and Android's preferences card: the value
 * rides the notification-settings read (unset = ON), the write is a PATCH carrying only this field, and
 * the switch is optimistic — it flips at once and goes back, with the save error, if the write fails.
 * Before the read lands the switch shows the default (ON), and a tap still writes.
 */
export function useInboxLearning() {
  const { data } = useNotificationSettings();
  const update = useUpdateNotificationSettings();
  const { logPageClickBi } = useGenericBi();
  const [pending, setPending] = useState<boolean | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  const stored = data?.learnFromConnectedInbox !== false;
  const enabled = pending ?? stored;

  const setEnabled = (next: boolean) => {
    logPageClickBi({ ...SETTINGS_PAGE, button_name: next ? 'enable_inbox_learning' : 'disable_inbox_learning' });
    setPending(next);
    update.mutate(
      { learnFromConnectedInbox: next },
      {
        // On success the cache holds the server's answer, so the override is dropped and the row reads it.
        onSuccess: () => setPending(null),
        onError: () => {
          setPending(null);
          setSaveFailed(true);
        },
      },
    );
  };

  return { enabled, setEnabled, saveFailed, dismissSaveFailed: () => setSaveFailed(false) };
}
